import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

import { catchError, filter, map, of, switchMap, take } from 'rxjs';

import { EntityService } from '@auth/service/entity.service';
import { NotificationService } from '@common/service/notification.service';
import { UserQuery } from '@common/store/user.store';

function buildCleanUrl(stateUrl: string, router: Router): UrlTree {
  const tree = router.parseUrl(stateUrl);
  delete tree.queryParams['entity'];
  return tree;
}

export const entitySwitchGuard: CanActivateFn = (route, state) => {
  const userQuery = inject(UserQuery);
  const entityService = inject(EntityService);
  const notificationService = inject(NotificationService);
  const router = inject(Router);

  const entityId = route.queryParamMap.get('entity');

  if (!entityId) {
    return true;
  }

  return userQuery.user$.pipe(
    filter((user) => !!user?.entity?.entity_id),
    take(1),
    switchMap((user) => {
      const cleanUrl = buildCleanUrl(state.url, router);

      console.debug('[EntitySwitchGuard] current entity_id:', user.entity.entity_id, '| requested:', entityId);

      if (user.entity.entity_id === entityId) {
        console.debug('[EntitySwitchGuard] already on requested entity — stripping param');
        return of<boolean | UrlTree>(cleanUrl);
      }

      console.debug('[EntitySwitchGuard] switching entity…');
      return entityService.switchEntity(entityId).pipe(
        map(() => {
          const companyName = userQuery.getValue().entity?.company_name ?? entityId;
          notificationService.notify({ message: `Switched to ${companyName}`, duration: 3000 });
          return cleanUrl as boolean | UrlTree;
        }),
        catchError((err) => {
          console.warn('[EntitySwitchGuard] Entity switch failed:', err);
          return of<boolean | UrlTree>(cleanUrl);
        }),
      );
    }),
  );
};
